import React, { memo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronRight, ChevronLeft } from 'lucide-react';

/**
 * Sidebar - Navigation sidebar component
 * @param {Object} props
 * @param {Array} props.items - Navigation items with { path, label, icon, exact }
 * @param {string} [props.title] - Optional sidebar title
 * @param {boolean} [props.collapsed] - Whether sidebar is collapsed
 * @param {Function} [props.onToggleCollapse] - Callback when collapse toggle is clicked
 * @param {string} [props.className] - Additional CSS classes
 */
export const Sidebar = memo(function Sidebar({ 
  items = [], 
  title, 
  collapsed = false,
  onToggleCollapse,
  className = '' 
}) {
  const navItemClasses = {
    base: 'flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group',
    inactive: 'text-base-content/70 hover:text-base-content hover:bg-base-200',
    active: 'bg-primary text-primary-content font-medium'
  };

  return (
    <aside className={`flex flex-col h-full transition-all duration-300 ${collapsed ? 'w-16' : 'w-64'} ${className}`}>
      <div className={`px-4 py-4 border-b border-base-200 flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
        {!collapsed && title && (
          <h2 className="text-lg font-semibold text-base-content">{title}</h2>
        )}
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className="p-1.5 rounded-lg hover:bg-base-200 transition-colors"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </button>
        )}
      </div>
      
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {items.map((item) => {
          const Icon = item.icon;
          const isExact = item.exact ?? true;
          
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={isExact}
              className={({ isActive }) =>
                `${navItemClasses.base} ${
                  isActive ? navItemClasses.active : navItemClasses.inactive
                } ${collapsed ? 'justify-center px-3' : ''}`
              }
              title={collapsed ? item.label : undefined}
            >
              {Icon && (
                <Icon className="w-5 h-5 flex-shrink-0" />
              )}
              {!collapsed && (
                <>
                  <span className="flex-1">{item.label}</span>
                  <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </>
              )}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
});

/**
 * SidebarLayout - Layout wrapper with sidebar
 * @param {Object} props
 * @param {React.ReactNode} props.sidebar - Sidebar content
 * @param {React.ReactNode} props.children - Main content
 * @param {boolean} [props.collapsible] - Whether sidebar can be collapsed
 * @param {string} [props.className] - Additional CSS classes
 */
export const SidebarLayout = memo(function SidebarLayout({
  sidebar,
  children,
  collapsible = true,
  className = ''
}) {
  // Start collapsed on mobile
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 1024; // lg breakpoint
    }
    return false;
  });

  const sidebarElement = React.isValidElement(sidebar)
    ? React.cloneElement(sidebar, {
        collapsed,
        onToggleCollapse: collapsible ? () => setCollapsed(!collapsed) : undefined
      })
    : sidebar;

  return (
    <div className={`flex overflow-hidden ${className}`}>
      <div className={`${collapsed ? 'w-16' : 'w-64'} bg-base-100 border-r border-base-200 flex-shrink-0 transition-all duration-300 sticky top-0 h-screen overflow-y-auto`}>
        {sidebarElement}
      </div>
      <div className={`flex-1 min-w-0 transition-all duration-300 overflow-hidden`}>
        {children}
      </div>
    </div>
  );
});

export default Sidebar;